import * as React from 'react'
// hooks
import { useShowModal } from 'hooks/index'
// constants
import { Modals } from 'constants/index'
// context
import { ActivitiesListAPIContext, NActivitiesListAPI } from 'containers/Activities/layers/API/ActivitiesList'
// types
import { ILog, LogEntityTypes } from 'types/graphql'
// utils
import { handleError } from 'utils/errors/handlers'
import { get, isEmpty } from 'lodash'
import { generateFilterVariables, getOptionsForFilter } from 'utils/index'

export declare namespace NActivitiesListBL {
  export interface IContext {
    filters: IFilter
    handleAddLinkClick: (activity: ILog) => void
    handleDeleteClick: (activity: ILog, refetch: NActivitiesListAPI.TActivitiesListRefetch) => void
    handleEditClick: (activity: ILog) => void
    handleSetFilters: (filters: IFilter) => void
    isReviewDetails: boolean
    isReadOnly: boolean
    isSubActivities: boolean
    shouldShowCommentForm: boolean
    shouldShowLogs: boolean
  }

  interface IFilter {
    [filter: string]: boolean
  }

  interface IIncomingProps {
    children?: React.ReactNode
    isReviewDetails?: boolean
    isReadOnly?: boolean
    isSubActivities?: boolean
    shouldShowCommentForm?: boolean
  }
}

export const ActivitiesListBLContext = React.createContext<NActivitiesListBL.IContext>({} as any)

const ActivitiesListBL: React.FunctionComponent<NActivitiesListBL.IIncomingProps> = ({
  children,
  isReviewDetails,
  isReadOnly,
  isSubActivities,
  shouldShowCommentForm = true
}) => {
  // context
  const { entity, refetch, deleteActivity, deleteResource, editActivity, addLink } =
    React.useContext(ActivitiesListAPIContext)

  // state
  const [shouldShowLogs, setShouldShowLogs] = React.useState<boolean>(true)
  const [filters, setFilters] = React.useState<NActivitiesListBL.IFilter>({
    attachments: true,
    comments: true,
    links: true,
    recordUpdates: false,
    resources: true
  })

  // redux
  const { showModal: showDeleteModal } = useShowModal({
    modalType: Modals.CONFIRM_DELETE_ACTIVITY_MODAL
  })

  const { showModal: showEditModal } = useShowModal({
    modalType: Modals.EDIT_ACTIVITY_MODAL
  })

  const { showModal: showEditResourceModal } = useShowModal({
    modalType: Modals.RESOURCE_CREATE_MODAL
  })

  const { showModal: showAddLinkModal } = useShowModal({
    modalType: Modals.ADD_LINK_FOR_ACTIVITY_MODAL
  })

  // methods
  const handleDeleteActivity = (activity) => {
    const shouldCreateActivity = !isReviewDetails && !isSubActivities

    showDeleteModal({
      handleConfirm: () => {
        if (activity.entityType === LogEntityTypes.Resource) {
          deleteResource(activity)
        } else {
          deleteActivity(activity, shouldCreateActivity)
        }
      },
      activity: activity
    })
  }

  const handleEditActivity = (activity) => {
    if (activity.entityType === LogEntityTypes.Resource) {
      showEditResourceModal({ entity, refetch, resource: get(activity, 'metadata.resource') })
      return
    }

    showEditModal({
      handleConfirm: (data) => {
        editActivity(activity, data)
      },
      activity: activity
    })
  }

  const handleAddLink = async (entity) => {
    showAddLinkModal({
      handleConfirm: (data) => {
        addLink(entity, data)
      }
    })
  }

  const applyFilter = async (filters) => {
    const enabledFilters = getOptionsForFilter(entity.entityType)
      .filter(({ stateKey }) => filters[stateKey])
      .map(({ value }) => value)

    const filter = generateFilterVariables(enabledFilters, entity)

    if (isEmpty(filter)) {
      return setShouldShowLogs(false)
    }

    try {
      await refetch({ filter } as any)
      setShouldShowLogs(true)
    } catch (errors) {
      handleError(errors, 'ActivitiesListBL.applyFilter')
    }
  }

  const handleSetFilters = (filters) => {
    setFilters(filters)
    if (entity.entityType !== LogEntityTypes.Review && !isSubActivities) {
      applyFilter(filters)
    }
  }

  const value = {
    filters,
    handleAddLinkClick: handleAddLink,
    handleDeleteClick: handleDeleteActivity,
    handleEditClick: handleEditActivity,
    handleSetFilters,
    isReviewDetails,
    isReadOnly,
    isSubActivities,
    shouldShowCommentForm,
    shouldShowLogs
  }

  return <ActivitiesListBLContext.Provider value={value}>{children}</ActivitiesListBLContext.Provider>
}

export default ActivitiesListBL
